const WEEKDAY_KEYS = [
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
];

const dateTimeFormat = timeZone =>
  new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

const part = (parts, type) => parts.find(item => item.type === type)?.value;

// Values keep the Ruby weekday (0 is Sunday); only the list starts on Monday.
const WEEK_DISPLAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

export const weekdayOptions = translate =>
  WEEK_DISPLAY_ORDER.map(wday => ({
    value: String(wday),
    label: translate(`INBOX_MGMT.RECONTACT.WEEKDAYS.${WEEKDAY_KEYS[wday]}`),
  }));

export const padTime = (hour, minute) =>
  `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;

// A time input always reports "HH:MM" in 24 hours, whatever the browser shows.
export const parseTime = value => {
  const match = /^(\d{2}):(\d{2})$/.exec(value || '');
  if (!match) return null;

  const hour = Number(match[1]);
  const minute = Number(match[2]);
  return hour <= 23 && minute <= 59 ? { hour, minute } : null;
};

export const weekdayLabel = (wday, translate) =>
  translate(`INBOX_MGMT.RECONTACT.WEEKDAYS.${WEEKDAY_KEYS[wday]}`);

const STATUS_LABELS = {
  open: 'INBOX_MGMT.RECONTACT.AUDIENCE.STATUS.OPEN',
  pending: 'INBOX_MGMT.RECONTACT.AUDIENCE.STATUS.PENDING',
  snoozed: 'INBOX_MGMT.RECONTACT.AUDIENCE.STATUS.SNOOZED',
  resolved: 'INBOX_MGMT.RECONTACT.AUDIENCE.STATUS.RESOLVED',
};

export const RECONTACT_STATUSES = Object.keys(STATUS_LABELS);

export const recontactStatusLabel = (status, translate) =>
  translate(STATUS_LABELS[status]);

export const toDatetimeLocal = (iso, timeZone) => {
  if (!iso) return '';
  const parts = dateTimeFormat(timeZone).formatToParts(new Date(iso));
  const hour = part(parts, 'hour') === '24' ? '00' : part(parts, 'hour');
  return `${part(parts, 'year')}-${part(parts, 'month')}-${part(parts, 'day')}T${hour}:${part(parts, 'minute')}`;
};

const offsetMs = (utcMs, timeZone) => {
  const parts = dateTimeFormat(timeZone).formatToParts(new Date(utcMs));
  const asUtc = Date.UTC(
    Number(part(parts, 'year')),
    Number(part(parts, 'month')) - 1,
    Number(part(parts, 'day')),
    Number(part(parts, 'hour')),
    Number(part(parts, 'minute')),
    Number(part(parts, 'second'))
  );
  return asUtc - utcMs;
};

const wallClockUtc = value => {
  const [date, time] = value.split('T');
  const [year, month, day] = date.split('-').map(Number);
  const [hour, minute] = time.split(':').map(Number);
  return Date.UTC(year, month - 1, day, hour, minute, 0);
};

export const fromDatetimeLocal = (value, timeZone) => {
  if (!value) return null;
  const wall = wallClockUtc(value);
  const instant = wall - offsetMs(wall - offsetMs(wall, timeZone), timeZone);
  return new Date(instant).toISOString();
};

export const formatInstant = (iso, timeZone) => {
  if (!iso) return '';
  return new Intl.DateTimeFormat(undefined, {
    timeZone,
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(iso));
};
